import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { emailMarketing } from '../../services/emailMarketingApi';

function personalize(value, firstName) {
  return String(value || '').replaceAll('{{first_name}}', firstName || 'Dancer');
}

export default function MarketingOptInPrompt({ user }) {
  const [prompt, setPrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sub = user?.attributes?.sub;
  const isAdmin = (user?.signInUserSession?.idToken?.payload?.['cognito:groups'] || []).includes('Admin');
  const firstName = user?.attributes?.given_name || user?.attributes?.name || '';

  useEffect(() => {
    if (!sub || isAdmin) return undefined;
    let active = true;
    // Delay the check until the page settles; this also avoids a duplicate claim in React Strict Mode.
    const timer = setTimeout(() => {
      emailMarketing('getOptInPrompt')
        .then((result) => { if (active && result.show) setPrompt(result.prompt); })
        .catch(() => { /* A reminder must never interrupt the learning experience. */ });
    }, 4000);
    return () => { active = false; clearTimeout(timer); };
  }, [sub, isAdmin]);

  const optIn = async () => {
    setSaving(true); setError('');
    try {
      const result = await emailMarketing('setPreference', { consent: true });
      if (!result.consent) throw new Error('Your address could not be opted in.');
      window.dispatchEvent(new CustomEvent('marketing-preference-changed', { detail: { consent: true } }));
      setPrompt(null);
    } catch (saveError) {
      setError('Your choice could not be saved. Please try again or use Email preferences in your dashboard.');
    } finally { setSaving(false); }
  };

  return <Dialog open={Boolean(prompt)} onClose={() => { if (!saving) setPrompt(null); }} maxWidth="sm" fullWidth aria-labelledby="marketing-opt-in-title" PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
    <Box sx={{ height: 8, background: 'linear-gradient(90deg, #137a3b, #f3c13a, #e1544c)' }} />
    <DialogTitle id="marketing-opt-in-title" sx={{ fontWeight: 800, fontSize: { xs: 24, sm: 28 }, pt: 3 }}>{personalize(prompt?.title, firstName)}</DialogTitle>
    <DialogContent>
      <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.65 }}>{personalize(prompt?.message, firstName)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Optional. You can unsubscribe at any time in your dashboard.</Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
      <Button onClick={() => setPrompt(null)} disabled={saving} sx={{ color: '#476354' }}>{prompt?.dismissLabel || 'Not now'}</Button>
      <Button variant="contained" onClick={optIn} disabled={saving} sx={{ backgroundColor: '#137a3b', fontWeight: 800, borderRadius: 2, '&:hover': { backgroundColor: '#0e6330' } }}>{saving ? 'Saving…' : prompt?.confirmLabel || 'Yes, send me updates'}</Button>
    </DialogActions>
  </Dialog>;
}
